'use client';

import { useEffect } from 'react';
import { strings } from '@/app/_components/locale';

/**
 * PortalError — Fehler-Boundary der Bewerber-Akte.
 *
 * Look 2026-08-02 (Gruender): am Marken-Loader (loading.tsx) ausgerichtet.
 * Vorher lag eine dunkle Navy-Karte (rgba(28,35,54,0.5)) ueber dem HELLEN
 * Portal-Default -> grau-auf-grau, kaum lesbar. Jetzt: heller Brand-Gradient
 * als Backdrop, solide Karte, hoher Kontrast; Dark-Mode-Override auf Cinema.
 */
export default function PortalError({
  error,
  reset,
}: {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
}) {
  useEffect(() => {
    // In Produktion landen Fehler in der Server-Log-Pipeline (Phase 2).
    // Hier nur ein Client-Log für die Dev-Console.
    // eslint-disable-next-line no-console
    console.error('[portal/error]', error);
  }, [error]);

  return (
    <main id="main" className="ps-overlay">
      <div className="ps-card">
        <div
          className="ps-badge ps-badge--danger"
          role="img"
          aria-label={strings.errorBoundary.title}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            width={30}
            height={30}
            aria-hidden="true"
          >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
        </div>

        <div className="ps-copy">
          <h1 className="ps-title">{strings.errorBoundary.title}</h1>
          <p className="ps-body">{strings.errorBoundary.body}</p>
          {error.digest && <p className="ps-ref">ref: {error.digest}</p>}
        </div>

        <button type="button" onClick={() => reset()} className="ps-btn">
          {strings.errorBoundary.retry}
        </button>
      </div>

      <style>{`
        /* === Light (Portal-Default) ==================================== */
        .ps-overlay {
          display: flex;
          min-height: 100dvh;
          align-items: center;
          justify-content: center;
          padding: 4rem 1.25rem;
          background:
            radial-gradient(circle at 25% 20%, rgba(15,110,86,0.10), transparent 60%),
            radial-gradient(circle at 75% 80%, rgba(46,191,162,0.08), transparent 60%),
            linear-gradient(180deg, #F5FAF6 0%, #EEF6F0 100%);
        }
        .ps-card {
          display: flex;
          width: 100%;
          max-width: 27rem;
          min-width: 0;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          border-radius: 20px;
          background: #FFFFFF;
          border: 1px solid rgba(15,110,86,0.12);
          box-shadow: 0 18px 50px rgba(31,122,78,0.14);
          padding: 2.5rem 1.75rem;
          text-align: center;
        }
        .ps-badge {
          display: flex;
          height: 60px;
          width: 60px;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .ps-badge--danger {
          background: rgba(229,72,77,0.12);
          color: #E5484D;
        }
        .ps-copy {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .ps-title {
          margin: 0;
          font-family: var(--font-display, system-ui, sans-serif);
          font-size: clamp(1.35rem, 4vw, 1.6rem);
          font-weight: 700;
          letter-spacing: -0.01em;
          line-height: 1.2;
          color: #0E1A13;
        }
        .ps-body {
          margin: 0;
          font-size: 14px;
          line-height: 1.55;
          color: #4A5568;
        }
        .ps-ref {
          margin: 0.25rem 0 0;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 12px;
          color: #98A1B3;
        }
        .ps-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: linear-gradient(135deg, #1f7a4e 0%, #155a39 100%);
          color: #FFFFFF;
          padding: 0.7rem 1.5rem;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          font-family: inherit;
          box-shadow: 0 8px 22px rgba(31,122,78,0.28);
          transition: transform .12s ease, box-shadow .12s ease;
        }
        .ps-btn:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(31,122,78,0.34); }
        .ps-btn:active { transform: translateY(0); }
        @media (prefers-reduced-motion: reduce) {
          .ps-btn { transition: none; }
          .ps-btn:hover { transform: none; }
        }

        /* === Dark (Cinema) Override ==================================== */
        :root[data-theme='dark'] .ps-overlay {
          background: linear-gradient(180deg, #0a0e1a 0%, #0E1A13 100%);
        }
        :root[data-theme='dark'] .ps-card {
          background: #131826;
          border-color: rgba(255,255,255,0.10);
          box-shadow: 0 18px 50px rgba(0,0,0,0.45);
        }
        :root[data-theme='dark'] .ps-title { color: #EAF0F6; }
        :root[data-theme='dark'] .ps-body { color: #C2C9D6; }
        :root[data-theme='dark'] .ps-ref { color: #6B7280; }
      `}</style>
    </main>
  );
}
